import { Storybook } from "@tento-ui/ui/storybook";
import { TurnResult } from "./result";

/** The first card is the one worth looking at, and it is deliberately
 * almost empty: a turn that went fine draws nothing at all.
 *
 * The replies around it are plain paragraphs rather than `turn.prose`.
 * A storybook card is bundled on its own and the prosemirror path map
 * resolves to a declaration file, so `std-prosemirror` never registers and
 * every `turn.prose` in a card renders zero-height — which would make the
 * card ABOUT an absence into a card that is entirely absent. Same class of
 * hazard as an `icons/*.svg` import in a card. */
export class TurnResultStoryBook extends Storybook.Default {
  title(): string {
    return "Turn · result";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Ok — nothing. The reply ending IS the ok",
        render: () => {
          const $wrap = column();
          $wrap.append(
            reply("Folded the six files into their owning commits."),
            // Zero pixels between these two lines. Both vendor surfaces
            // leave a successful turn unmarked; a mark on every turn is a
            // mark nobody reads.
            TurnResult.makeWith({
              status: "ok",
              text: "end_turn · session afb69f67",
            }),
            reply("There is a result between these two replies."),
          );
          return $wrap;
        },
      },
      {
        title: "Suspended — the one ending a reader cannot infer",
        render: () =>
          TurnResult.makeWith({
            status: "suspended",
            text: "Parked — approval apr-311 pending",
          }),
      },
      {
        title: "Suspended, with what stopped it behind the chevron",
        render: () =>
          TurnResult.makeWith({
            status: "suspended",
            text: "Stopped — the turn hit its step budget",
            detail: [
              "steps: 40/40",
              "last: execute cargo test -p tento-spool",
              "resume: tsh session resume 9f2c41a8",
            ].join("\n"),
          }),
      },
      {
        title: "Error — the one loud face, delegated to std-exception",
        render: () =>
          TurnResult.makeWith({
            status: "error",
            text: "ValidationError: unit rejected after attempt 3/3",
          }),
      },
      {
        title: "Error with the record's body underneath, verbatim",
        render: () =>
          TurnResult.makeWith({
            status: "error",
            text:
              "10 of 14 statements failed — lisp error: lane.declare: lane deepseek: no handler \"deepseek\" registered",
            detail: [
              '[line 13] (lane.declare "deepseek")',
              "  -> 10 of 14 statements failed — lisp error: lane.declare: lane",
              '     deepseek: no handler "deepseek" registered (+9 more, each warned)',
            ].join("\n"),
          }),
      },
      {
        title: "Three turns in sequence — how a scrollback actually reads",
        render: () => {
          const $wrap = column();
          $wrap.append(
            reply("Rebuilt the preview bundle."),
            TurnResult.makeWith({ status: "ok", text: "end_turn · afb69f67" }),
            reply("Re-ran the check."),
            TurnResult.makeWith({ status: "ok", text: "end_turn · 3c1d0a24" }),
            reply("Tried the third."),
            TurnResult.makeWith({
              status: "error",
              text: "lisp parse error: unterminated string",
              detail: "[line 1] lets rename this terminal instance",
            }),
          );
          return $wrap;
        },
      },
    ];
  }
}

/** A stand-in for the reply a result closes — see the note at the top for
 * why this is not `turn.prose`. */
function reply(text: string): HTMLElement {
  const $p = document.createElement("div");
  $p.textContent = text;
  $p.style.fontSize = "13px";
  $p.style.lineHeight = "1.5";
  $p.style.color = "var(--text-pri, #1f2328)";
  return $p;
}

function column(): HTMLElement {
  const $wrap = document.createElement("div");
  $wrap.style.display = "flex";
  $wrap.style.flexDirection = "column";
  $wrap.style.gap = "10px";
  $wrap.style.width = "100%";
  return $wrap;
}
